import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { uniqBy } from 'lodash';
import { MessageDirection } from '../lib/enums/message';
import { CommunicationPreference } from '../../shared/lib/enums/communicationPreference';

export const state = {
  threads: [],
  messages: [],
  unreadThreadsCount: 0,
  isIncomingMessageOnly: false,
  unreadCount: undefined,
  archivedUnreadCount: undefined,
  unsubscribedUnreadCount: undefined,
  nextPage: undefined,
  contactsNextPage: undefined,
  searchContacts: []
};

export const getters = {
  latestIncomingMessage(state) {
    return state.messages
      .filter(x => x.direction != MessageDirection.Outgoing)
      .slice(-1)[0];
  },
  messagesContactId(state) {
    return state.messages?.length ? state.messages[0].contactId : undefined;
  }
};

export const mutations = {
  setThreads(state: any, threads: any[]): void {
    state.threads = threads;
  },

  setMessages(state: any, { messages }): void {
    state.messages = messages;
  },

  setUnreadThreadsCount(state: any, { unreadThreadsCount }): void {
    state.unreadThreadsCount = unreadThreadsCount;
  },

  addMessage(state: any, message): void {
    state.messages.push(message);
  },

  setThreadAsRead(state: any, { contactId }): void {
    state.threads.find(x => x.contactId === contactId).isRead = true;
  },

  setThreadCounts(state, data) {
    state.unreadCount = data.unreadCount;
    state.archivedUnreadCount = data.archivedUnreadCount;
    state.unsubscribedUnreadCount = data.unsubscribedUnreadCount;
  },
  removeThread(state: any, { contactId }): void {
    state.threads = state.threads.filter(
      thread => thread.contactId !== contactId
    );
  },
  setThread(state: any, updatedThread): void {
    const threadIndex = state.threads.findIndex(
      thread => thread.id === updatedThread.id
    );
    state.threads[threadIndex] = updatedThread;
  },
  setNextPage(state: any, data): void {
    state.nextPage = data?.nextPages?.length ? data.nextPages[0] : undefined;
  },
  setContactsNextPage(state: any, data): void {
    state.contactsNextPage = data?.nextPages?.length
      ? data.nextPages[0]
      : undefined;
  },
  updateThreadContact(state: any, contact): void {
    const index = state.threads.findIndex(x => x.id === contact.id);
    if (index < 0) return;
    state.threads[index].contact.firstName = contact.first_name;
    state.threads[index].contact.lastName = contact.last_name;
  },
  setSearchContacts(state: any, contacts) {
    state.searchContacts = contacts;
  }
};

export const actions = {
  async loadThreads(
    { commit },
    { filter, contactId, searchTerm }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).get(
        '/data-api/threads-index',
        {
          params: {
            'page[limit]': 25,
            ...(filter && filter),
            ...(contactId && { includeFor: contactId }),
            ...(searchTerm && {
              'filter[__search:contact.firstName,contact.phone][val]': searchTerm,
              'filter[__search:contact.firstName,contact.phone][op]': 'search'
            })
          }
        }
      );

      const threads = response.data.data.map(x => x.attributes);
      commit('setThreads', threads);
      commit('setThreadCounts', response.data.meta);
      commit('setNextPage', response.data.links);

      if (response.data.included?.length) {
        commit('setMessages', {
          messages: response.data.included.map(x => x.attributes)
        });
      }

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadContactsAsThreads(
    { dispatch, commit, state },
    { searchTerm, pageSize }
  ) {
    const response = await dispatch(
      'contact/getContacts',
      {
        communicationPreference: CommunicationPreference.Subscribed,
        searchTerm,
        pageSize
      },
      { root: true }
    );

    const contacts = response.data.data
      .filter(contact => !state.threads.find(x => x.id === contact.id))
      .map(x => ({
        id: x.id,
        contact: {
          id: x.id,
          phone: x.attributes.phone,
          firstName: x.attributes.first_name,
          lastName: x.attributes.last_name,
          communication_preference: x.attributes.communication_preference
        },
        contactId: x.id,
        isRead: true
      }));
    commit('setSearchContacts', contacts);
    commit('setContactsNextPage', response.data.links);
  },

  async loadContactsNextPage({ state, commit }) {
    try {
      const response = await createAxios().get(state.contactsNextPage.path);
      const newThreads = response.data.data.map(x => ({
        id: x.id,
        contact: {
          id: x.id,
          phone: x.attributes.phone,
          firstName: x.attributes.first_name,
          lastName: x.attributes.last_name,
          communication_preference: x.attributes.communication_preference
        },
        contactId: x.id,
        isRead: true
      }));
      const contacts = uniqBy(
        [...state.threads, ...state.searchContacts, ...newThreads],
        'id'
      );
      commit('setSearchContacts', contacts);
      commit('setContactsNextPage', response.data.links);
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadNextPage({ state, commit }) {
    try {
      const response = await createAxios(process.env.DataApiBaseUrl).get(
        state.nextPage.path
      );
      const newThreads = response.data.data.map(x => x.attributes);
      const threads = uniqBy([...state.threads, ...newThreads], 'id');
      commit('setThreads', threads);
      commit('setNextPage', response.data.links);
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadMessages({ commit }, contactId): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).get(
        '/data-api/messages-index',
        {
          params: {
            contactId
          }
        }
      );

      const messages = response.data.data.map(x => x.attributes);
      commit('setMessages', {
        messages
      });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadUnreadThreadsCount({ commit }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).get(
        '/data-api/thread-counts-show'
      );
      const counts = response.data.data.attributes;
      commit('setUnreadThreadsCount', counts);

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async sendSms(
    { commit },
    { contactId, mmsTemplateId, body }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/contact-sms-sends-create',
        {
          data: {
            type: 'sms',
            attributes: {
              body,
              mmsTemplateId
            }
          }
        },
        {
          params: {
            id: contactId
          }
        }
      );
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async readThread(
    { commit },
    { contactId, messageId }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).patch(
        '/data-api/thread-reads-create',
        {},
        {
          params: {
            contactId
          }
        }
      );
      commit('setThreadAsRead', { contactId });
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async updateThread({ commit }, { contactId, body }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).patch(
        '/data-api/threads-update',
        {
          data: {
            type: 'thread',
            attributes: body
          }
        },
        {
          params: {
            contactId: contactId
          }
        }
      );
      commit('setThread', response.data.data);
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async createThread({ commit }, { clientId, body }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).post(
        '/data-api/threads-create',
        {
          data: {
            type: 'thread',
            attributes: body
          }
        },
        {
          params: {
            clientId
          }
        }
      );
      commit('setThread', response.data.data);
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async unsubscribeContact({ commit }, { contactId }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).patch(
        '/data-api/contact-unsubscribes-create',
        {},
        {
          params: {
            contactId: contactId
          }
        }
      );
      commit('removeThread', { contactId });
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
